import {
  createContext, useContext, useMemo, useState, useCallback
} from 'react';

import PropTypes from 'prop-types';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useIntl } from '@zenbusiness/application-commons-intl';
import { ZBError } from '@zenbusiness/application-commons-logging';

import { useDashboardPath } from '#client/hooks/useDashboardPath';
import { VELO_BANNER_DEFAULT } from '#client/pages/ProductLanding/components/HeroSection/constants';
import { useProductLandingContext } from '#client/pages/ProductLanding/contexts/useProductLanding';
import { useAdpReferralCtaState } from '#client/pages/ProductLanding/hooks/useAdpReferralCtaState';
import { useProductCoupon } from '#client/pages/ProductLanding/hooks/useProductCoupon';

// Helper function to convert package name to URL-safe format
const formatPackageNameForUrl = (packageName) => packageName
  .replace(/\+/g, 'and') // Replace + with 'and'
  .replace(/ /g, '-') // Replace spaces with dashes
  .toLowerCase();

const BuyBoxContext = createContext();

export const useBuyBoxContext = () => {
  const context = useContext(BuyBoxContext);

  if (!context) {
    throw new ZBError('useBuyBoxContext must be used within a BuyBoxProvider');
  }

  return context;
};

export const BuyBoxProvider = ({ children }) => {
  const productLandingContext = useProductLandingContext();
  const {
    productId,
    entitlements,
    orderTasksWithStatus,
    packages,
    defaultPackageIndex = 0,
    comparePackages,
    plans,
    getBuyButtonConfig
  } = productLandingContext;

  const {
    businessEntityUuid,
    businessEntity: {
      domesticJurisdiction, formedBy, formationStatus, type
    }
  } = useBusinessEntity();

  const { formatMessage: fm } = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const dashboardPath = useDashboardPath();
  const navigate = useNavigate();

  // Package selection logic
  const packageName = searchParams.get('package');
  const packageIndex = packages?.findIndex(
    (pkg) => pkg.packageName && formatPackageNameForUrl(fm({ id: pkg.packageName })) === packageName
  ) ?? -1;

  // Validate defaultPackageIndex is within bounds of packages array
  const isValidDefaultIndex = defaultPackageIndex >= 0
    && packages?.length > 0
    && defaultPackageIndex < packages.length;
  const safeDefaultIndex = isValidDefaultIndex ? defaultPackageIndex : 0;
  const initialPackageIndex = packageIndex !== -1 ? packageIndex : safeDefaultIndex;

  const [packageActive, setPackageActive] = useState(initialPackageIndex);
  const isAdpProduct = productId === 'adp';
  const { adpReferralSubmitted, markAdpReferralSubmitted } = useAdpReferralCtaState({
    isAdpProduct,
    businessEntityUuid
  });
  const ctaConfigContext = useMemo(
    () => (isAdpProduct ? { adpReferralSubmitted } : {}),
    [isAdpProduct, adpReferralSubmitted]
  );

  // Active package data (memoized)
  const activePackage = useMemo(() => packages?.[packageActive] || {}, [packages, packageActive]);

  const {
    bulletPoints,
    ctaIcon,
    couponBanner,
    interactService = {},
    note,
    productDescription,
    packageNote,
    packageLabel,
    packageColor = '',
    productName,
    showPreviousPurchasedCallout,
    veloBanner = VELO_BANNER_DEFAULT,
    textLink,
    interactTextLink = {}
  } = activePackage;

  const {
    ctaType,
    service,
    openInNewTab = false,
    isBusinessTypeDependent = false,
    isPurchasedByOrderTask = false,
    interval
  } = interactService;

  // Resolve bulletPoints if it's a function (tax classification dependent)
  const resolvedBulletPoints = typeof bulletPoints === 'function'
    ? bulletPoints(productLandingContext)
    : bulletPoints;

  // Service code logic
  const serviceCode = useMemo(() => {
    if (!service) return '';

    // Dynamic service resolver function
    if (typeof service === 'function') {
      return service(productLandingContext);
    }

    // Business type prefix pattern
    if (isBusinessTypeDependent) {
      return `${type}_${service}`;
    }

    return service;
  }, [service, productLandingContext, isBusinessTypeDependent, type]);

  const finalCtaType = useMemo(() => (typeof ctaType === 'function' ? ctaType(serviceCode) : ctaType), [ctaType, serviceCode]);

  // Enhanced entitlements with order task services
  const enhancedEntitlements = useMemo(() => {
    if (!isPurchasedByOrderTask || !orderTasksWithStatus?.length) {
      return entitlements;
    }

    // Check if service exists in orderTasksWithStatus
    const hasServiceInOrderTasks = orderTasksWithStatus.some(
      (orderTask) => orderTask.type === serviceCode || orderTask.data?.service === serviceCode
    );

    if (!hasServiceInOrderTasks) {
      return entitlements;
    }

    // Add service code to entitlements
    return {
      ...entitlements,
      has: (key) => {
        if (key === serviceCode) return true;
        return entitlements?.has(key);
      }
    };
  }, [entitlements, isPurchasedByOrderTask, orderTasksWithStatus, serviceCode]);

  const { enabledCoupon, serviceToAddToCart } = useProductCoupon({
    entitlements: enhancedEntitlements,
    serviceCode,
    interval
  });

  // Pricing and CTA props
  const ctaProps = useMemo(
    () => getBuyButtonConfig?.(
      enhancedEntitlements,
      serviceCode,
      domesticJurisdiction,
      formedBy,
      formationStatus,
      ctaConfigContext
    ) || {},
    [getBuyButtonConfig, enhancedEntitlements, serviceCode,
      domesticJurisdiction, formedBy, formationStatus, ctaConfigContext]
  );
  const onCtaActionSuccess = useCallback(({ ctaType: actionCtaType }) => {
    if (actionCtaType !== 'adpReferral') {
      return;
    }

    markAdpReferralSubmitted();
  }, [markAdpReferralSubmitted]);

  const onCtaActionFailure = useCallback(() => {
  }, []);

  const onCtaActionComplete = useCallback(() => {
  }, []);

  // Computed values
  const displayCallout = Boolean(enhancedEntitlements?.has(service)
    && showPreviousPurchasedCallout);

  // Coupon banner logic - show when coupons are available for this product
  const displayCouponBanner = Boolean(couponBanner && serviceToAddToCart?.couponUuid);
  const couponBannerMessage = couponBanner?.message;

  // Event handlers (memoized)
  const handleTabChange = useCallback((_, newValue) => {
    setPackageActive(newValue);
    const pkgName = fm({ id: packages[newValue].packageName });
    setSearchParams({ package: formatPackageNameForUrl(pkgName) });
  }, [packages, fm, setSearchParams]);

  const handleComparePackagesClick = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('m', 'comparePackages');
    if (plans) setSearchParams(newParams);
  }, [searchParams, plans, setSearchParams]);

  const handleTextLinkClick = useCallback(() => {
    if (!interactTextLink?.url) return;
    if (interactTextLink.target === 'external') {
      window.open(interactTextLink.url, '_blank');
      return;
    }

    navigate(`${dashboardPath}${interactTextLink.url}`);
  }, [interactTextLink, navigate, dashboardPath]);

  const contextValue = useMemo(() => ({
    // Package data
    packages,
    packageActive,
    activePackage,
    comparePackages,
    plans,

    // Active package properties
    bulletPoints: resolvedBulletPoints,
    productDescription,
    ctaIcon,
    ctaType: finalCtaType,
    couponBannerMessage,
    displayCouponBanner,
    note,
    packageNote,
    packageLabel,
    packageColor,
    productName,
    serviceCode,
    openInNewTab,
    interval,
    enabledCoupon,
    serviceToAddToCart,
    veloBanner,
    ctaConfigContext,
    onCtaActionSuccess,
    onCtaActionFailure,
    onCtaActionComplete,
    // Computed values
    ctaProps,
    displayCallout,

    // Event handlers
    handleTabChange,
    handleComparePackagesClick,
    textLink,
    handleTextLinkClick
  }), [
    packages,
    packageActive,
    activePackage,
    comparePackages,
    plans,
    resolvedBulletPoints,
    productDescription,
    ctaIcon,
    finalCtaType,
    note,
    packageNote,
    packageLabel,
    packageColor,
    productName,
    serviceCode,
    openInNewTab,
    ctaProps,
    displayCallout,
    displayCouponBanner,
    couponBannerMessage,
    handleTabChange,
    handleComparePackagesClick,
    interval,
    enabledCoupon,
    serviceToAddToCart,
    veloBanner,
    ctaConfigContext,
    onCtaActionSuccess,
    onCtaActionFailure,
    onCtaActionComplete,
    textLink,
    handleTextLinkClick
  ]);

  return (
    <BuyBoxContext.Provider value={contextValue}>
      {children}
    </BuyBoxContext.Provider>
  );
};

BuyBoxProvider.propTypes = {
  children: PropTypes.node.isRequired
};
